import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { Chip } from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import { EmptyState, Loading, PageHeader } from '~/components';
import { RelativeTime } from '~/elements';
import { useNavigate } from '~/lib/navigation';
import { CardToken, CardTokenRepository } from '~/repositories/CardTokenRepository';
import { useSessionStore } from '~/stores/Session';

const TableContainer = styled.div`
  padding: 0 var(--page-margin);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.secondary.blendToBackground(600)};
  border-bottom: 1px solid ${({ theme }) => theme.secondary.blendToBackground(100)};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Td = styled.td`
  padding: 16px;
  font-size: 14px;
  color: ${({ theme }) => theme.foreground};
  border-bottom: 1px solid ${({ theme }) => theme.secondary.blendToBackground(100)};
`;

const Tr = styled.tr`
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.secondary.blendToBackground(50)};
  }
`;

const StatusChip = styled(Chip)<{ $status: string }>`
  text-transform: capitalize;
`;

const WalletChip = styled(Chip)`
  text-transform: capitalize;
`;

const formatStatus = (status: string): string => {
  if (!status) return '-';
  return status.toLowerCase().replace(/_/g, ' ');
};

const formatWalletType = (walletType: string): string => {
  if (!walletType) return '-';
  return walletType.toLowerCase().replace(/_/g, ' ');
};

export const PageCardTokensOverview: React.FC = () => {
  const navigate = useNavigate();
  const { currentPlatform, isSandbox } = useSessionStore();
  const [cardTokens, setCardTokens] = useState<CardToken[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTokens = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await CardTokenRepository.listCardTokens();
        setCardTokens(response.cardTokens || []);
      } catch (err) {
        console.error('Error fetching card tokens:', err);
        setError('Failed to load card tokens');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTokens();
  }, [currentPlatform?.id, isSandbox]);

  const renderTable = () => {
    if (cardTokens.length === 0) {
      return <EmptyState headline="No card tokens" text="No card tokens found." />;
    }

    return (
      <Table>
        <thead>
          <tr>
            <Th>Token ID</Th>
            <Th>Card ID</Th>
            <Th>Wallet Type</Th>
            <Th>Token Type</Th>
            <Th>Device Name</Th>
            <Th>Status</Th>
            <Th>Created</Th>
          </tr>
        </thead>
        <tbody>
          {cardTokens.map((token) => (
            <Tr key={token.id} onClick={() => navigate(`${ROUTE.CARD_TOKENS}/edit/${token.id}`)}>
              <Td>{token.id}</Td>
              <Td>{token.cardId || '-'}</Td>
              <Td>
                <WalletChip>{formatWalletType(token.walletType)}</WalletChip>
              </Td>
              <Td>{token.tokenType ? formatStatus(token.tokenType) : '-'}</Td>
              <Td>{token.deviceName || '-'}</Td>
              <Td>
                <StatusChip $status={token.status}>{formatStatus(token.status)}</StatusChip>
              </Td>
              <Td>{token.createdAt ? <RelativeTime timestamp={token.createdAt} /> : '-'}</Td>
            </Tr>
          ))}
        </tbody>
      </Table>
    );
  };

  return (
    <>
      <PageHeader text="Card Tokens" border={false} />
      <Loading isLoading={isLoading}>
        {error ? <EmptyState headline="Error" text={error} /> : <TableContainer>{renderTable()}</TableContainer>}
      </Loading>
    </>
  );
};
